/**
 * `mirror` → **Mirror column settings** (spec M2 tier-2 §5.18, card P-123).
 *
 * The second visibly-broken type of P-123. An unconfigured mirror's panel offers one control,
 * **Connect**, which dispatches `openColumnSettings(column.id)`; the request channel worked, the
 * table view was listening — and `ColumnSettingsHost` rendered `null`, because `mirror` had no
 * dialog. So the button opened nothing, which is exactly what round C2 asserts
 * (*`mirror-connect` opens the column settings rather than writing a value*).
 *
 * `settings_str`, verbatim `[cvs§2 mirror]`, `[09§A.7 mirror]`:
 *
 *  - `relation_column`: `{"<board_relation column id>": true}` or `{"subitems": true}` — the
 *    relation the reflection travels along. Offered as a list of **this board's own** relation
 *    columns, which is data the dialog has.
 *  - `displayed_linked_columns`: `{"<far-side board id>": ["<column id>", …]}` — the columns read
 *    on the other side. The board view holds no columns of the *far* board (nothing loads them),
 *    so they are edited as monday's own comma-separated ids per board rather than as a picker
 *    that would have to invent a vocabulary. A far-side column picker needs a `boards.columns`
 *    read the table source does not do yet — q526.
 *  - `sumType`: how several reflected values fold into one.
 *
 * Only `relation_column` is exactly one entry: `mirrorRelation` takes the first `true` and the
 * dialog writes a single-key map, so a mirror can never end up following two relations at once.
 */
import {
  mirrorRelation,
  type MirrorSettings,
} from '@elizmonday/shared';
import {
  HideSummaryField,
  SettingsSection,
  SettingsSelect,
  SettingsField,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

/** The folds monday names for a mirror `[cvs§2 mirror]`; `""` is "show them all". */
const SUM_TYPES = [
  { value: '', label: 'Show every value' },
  { value: 'allStatuses', label: 'All statuses' },
  { value: 'sum', label: 'Sum' },
  { value: 'average', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
];

/** `{board: ["a","b"]}` → the `board: a, b` lines the ids field shows. */
function toLines(map: Record<string, string[]>): string {
  return Object.entries(map)
    .map(([boardId, columnIds]) => `${boardId}: ${columnIds.join(', ')}`)
    .join('\n');
}

/** The inverse. A line with no `:` is ignored rather than stored under an empty board id. */
export function parseDisplayedColumns(text: string): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const line of text.split('\n')) {
    const at = line.indexOf(':');
    if (at < 0) continue;
    const boardId = line.slice(0, at).trim();
    if (boardId === '') continue;
    const columnIds = line
      .slice(at + 1)
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry !== '');
    if (columnIds.length > 0) map[boardId] = columnIds;
  }
  return map;
}

export function MirrorSettingsDialog({
  columns = [],
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<MirrorSettings>) {
  const [draft, patch] = useSettingsDraft<MirrorSettings>(settings);
  const relation = mirrorRelation(draft);
  const current = relation === null ? '' : relation.kind === 'subitems' ? 'subitems' : relation.columnId;

  /*
   * The relations a mirror may follow on **this** board: every `board_relation` column, plus
   * monday's pseudo-key `subitems` when the board has a subitems column. The dialog reads them
   * off the board's own columns rather than branching on a type name anywhere else.
   */
  const relations = [
    { value: '', label: 'Not connected' },
    ...columns
      .filter((entry) => entry.type === 'board_relation')
      .map((entry) => ({ value: entry.id as string, label: entry.title })),
    ...(columns.some((entry) => entry.type === 'subtasks')
      ? [{ value: 'subitems', label: 'Subitems of this item' }]
      : []),
  ];

  const displayed = draft.displayed_linked_columns ?? {};
  const displayedMap = Array.isArray(displayed) ? {} : displayed;

  return (
    <SettingsForm
      title="Mirror column settings"
      testId="mirror-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SettingsSelect
        label="Reflect through"
        testId="mirror-relation"
        value={current}
        options={relations}
        onChange={(next) =>
          patch({ relation_column: next === '' ? {} : { [next]: true } })
        }
      />
      {relations.length === 1 ? (
        <p className="ez-type-settings__empty" data-testid="mirror-no-relations">
          This board has no Connect boards column yet. Add one, connect it to a board, and this
          mirror can reflect a column from it.
        </p>
      ) : null}

      <SettingsSection label="Columns to reflect" />
      <SettingsField
        label="One line per connected board: its id, a colon, then the column ids"
        testId="mirror-displayed-columns"
        value={toLines(displayedMap)}
        placeholder="5939123543: status1, date4"
        onChange={(next) => patch({ displayed_linked_columns: parseDisplayedColumns(next) })}
      />

      <SettingsSelect
        label="When several items are linked"
        testId="mirror-sum-type"
        value={draft.sumType ?? ''}
        options={SUM_TYPES}
        onChange={(next) => patch({ sumType: next })}
      />

      <HideSummaryField
        testId="mirror-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
